import { FC, useEffect, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
	Typography,
} from '@mui/material';
import { ContentCopy, OpenInNew } from '@mui/icons-material';
import { api } from 'services/api';
import { getPgErrorMessage } from 'services/api/paymentGateway/errors';
import { TPgPaymentLink } from 'services/api/paymentGateway/types';
import { notificationContainer } from 'utils/notificationContainer';
import { PgMiniTable } from '../common/PgMiniTable';
import { copyText } from '../common/helpers';
import { formatMoney } from '../utils';

type TPaymentLinkDialogProps = {
	open: boolean;
	onClose: () => void;
};

/**
 * Payment link generator (03 §4.3): the public payment page of an invoice by its number. The link
 * carries the encrypted invoice number; open invoices of the same number are listed.
 */
export const PaymentLinkDialog: FC<TPaymentLinkDialogProps> = ({ open, onClose }) => {
	const [invoiceNumber, setInvoiceNumber] = useState('');
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [link, setLink] = useState<TPgPaymentLink | null>(null);

	useEffect(() => {
		if (!open) return;
		setInvoiceNumber('');
		setError(null);
		setLink(null);
		setBusy(false);
	}, [open]);

	const parsed = /^\d+$/.test(invoiceNumber.trim()) ? Number(invoiceNumber.trim()) : null;

	const generate = async () => {
		if (parsed === null) return;
		setBusy(true);
		setError(null);
		setLink(null);
		try {
			setLink(await api.paymentGateway.createPaymentLink({ invoiceNumber: parsed }));
		} catch (e) {
			setError(getPgErrorMessage(e, 'Failed to create the payment link'));
		} finally {
			setBusy(false);
		}
	};

	const copy = async () => {
		if (!link) return;
		const copied = await copyText(link.paymentLink);
		notificationContainer(
			copied ? 'Link copied' : 'Copy failed: select the link manually',
			copied ? 'success' : 'error',
		);
	};

	return (
		<Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
			<DialogTitle color="primary">Payment link</DialogTitle>
			<DialogContent>
				<Typography variant="body2" sx={{ mb: 2 }}>
					Creates the link to the payment page of an invoice. Send it to the customer: the customer
					pays by card, Apple Pay, Google Pay or Direct Debit.
				</Typography>
				<Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
					<TextField
						label="Invoice number"
						value={invoiceNumber}
						onChange={(e) => setInvoiceNumber(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === 'Enter') void generate();
						}}
						size="small"
						sx={{ minWidth: 220 }}
						disabled={busy}
					/>
					<Button
						variant="contained"
						onClick={() => void generate()}
						disabled={parsed === null || busy}
						startIcon={busy ? <CircularProgress size={16} /> : undefined}
					>
						Generate
					</Button>
				</Box>
				{error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{error}
					</Alert>
				)}
				{link && (
					<>
						<TextField
							label="Payment link"
							value={link.paymentLink}
							size="small"
							fullWidth
							InputProps={{ readOnly: true }}
							onFocus={(e) => e.target.select()}
						/>
						<Box sx={{ display: 'flex', gap: 1, my: 1 }}>
							<Button size="small" startIcon={<ContentCopy />} onClick={() => void copy()}>
								Copy
							</Button>
							<Button
								size="small"
								href={link.paymentLink}
								target="_blank"
								rel="noopener noreferrer"
								startIcon={<OpenInNew />}
							>
								Open
							</Button>
						</Box>
						<Typography variant="subtitle2" color="primary" sx={{ mt: 1 }}>
							Invoices behind the link
						</Typography>
						<PgMiniTable
							rows={link.invoices}
							getKey={(row) => row.invoiceId}
							emptyText="No unpaid invoices with this number"
							columns={[
								{ header: 'Invoice ID', render: (row) => row.invoiceId },
								{ header: 'Service type', render: (row) => row.serviceType },
								{
									header: 'Outstanding',
									align: 'right',
									render: (row) => formatMoney(row.outstandingAmount, 'EUR'),
								},
							]}
						/>
					</>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Close</Button>
			</DialogActions>
		</Dialog>
	);
};
